'use client'

import { useContext } from 'react'
import { WebSocketContext } from '@/providers'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { UserType } from '@/types/user'
import { cn } from '@/lib/utils'

type UserAvatarProps = {
  user: Pick<UserType, 'id' | 'firstname' | 'lastname' | 'online'>
  size?: 'sm' | 'md' | 'lg'
  className?: string
  showOnlineStatus?: boolean
}

const sizeClasses = {
  sm: 'h-8 w-8',
  md: 'h-12 w-12',
  lg: 'h-16 w-16'
}

const indicatorSizes = {
  sm: 'h-2.5 w-2.5',
  md: 'h-3 w-3',
  lg: 'h-4 w-4'
}

const textSizes = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg'
}

const UserAvatar = ({ user, size = 'md', className, showOnlineStatus = true }: UserAvatarProps) => {
  // Use context directly instead of hook to handle cases where the provider might not be available
  const context = useContext(WebSocketContext)
  const userStatuses = context?.userStatuses ?? {}

  // Check if user status was updated via WebSocket, fallback to user.online from API
  const isOnline = userStatuses[user.id] !== undefined ? userStatuses[user.id] : user.online

  return (
    <div className={cn('relative', className)}>
      <Avatar className={sizeClasses[size]}>
        <AvatarFallback className={cn('font-semibold', textSizes[size])}>
          {user.firstname.charAt(0)}
          {user.lastname.charAt(0)}
        </AvatarFallback>
      </Avatar>
      {showOnlineStatus && (
        <span
          className={cn(
            'absolute right-0 bottom-0 rounded-full border-2 border-white dark:border-gray-800',
            indicatorSizes[size],
            isOnline ? 'bg-green-500' : 'bg-gray-500'
          )}
        />
      )}
    </div>
  )
}

export default UserAvatar
